import { Provider } from '@angular/core';
import {
  SensorToolbarConfig,
  SENSOR_TOOLBAR_CONFIGS,
} from '../../shared/contracts/toolbar-control.tokens';
import { VR_OPERATOR_KEY } from './vr-toolbar-controls.providers';

/**
 * VOYAGE RADAR'S TOOLBAR COMPOSITION — which controls this sensor shows, in
 * which slot, in what order (D13).
 *
 * Split out of `shared/toolbar/sensor-toolbar.configs.ts`, which held every
 * operator's bars in one shared file. `SENSOR_TOOLBAR_CONFIGS` was already a
 * `multi` token, so the single array was a convention rather than a
 * constraint — exactly as `TOOLBAR_CONTROLS` turned out to be in D9. A sensor
 * added after this one writes its own file beside its own controls and touches
 * nothing here.
 *
 * It also retires the last vendor names in `ais/shared/`: those six
 * `operatorKey` literals were the one documented exception to "shared names no
 * provider", carried since the beginning.
 *
 * `operatorKey` is IMPORTED rather than restated. It has to equal the string
 * the host matches descriptors' `operatorKey` against, and while the config
 * lived in `shared/` it could not import from `voyage-radar/` (R5) — so D9 had
 * to write the literal twice and document the drift risk. Both halves are in
 * this folder now, so the risk is gone rather than managed.
 *
 * Explicit `order` throughout rather than the descriptors' defaults. Two
 * reasons: the transport reads left-to-right as ⏮ ◀ ▶ ▶ ⏭ with the scrub rail
 * AFTER it, which is not the order the ids were registered in; and separators
 * only mean something relative to their neighbours, so a bar carrying them has
 * to state every position anyway. With orders spelled out, this list also reads
 * in the order it renders.
 *
 * KEEPING A CONTROL IN THE SAME PLACE ACROSS SENSORS is now a cross-file
 * concern rather than something visible at a glance — `clusters` sits at 17 and
 * 82 on both this sensor and AISstream, and that is deliberate. The shared
 * descriptor's `defaultOrder` is the canonical position; a per-operator `order`
 * that merely restates it is free to be dropped, and one that differs should
 * say why.
 */

/**
 * Voyage Radar: pull-based (no live stream). Four slots:
 *   live     — filter, labels, grouping, the track entry, and the way into
 *              snapshot playback.
 *   playback — replay of the saved AOI refresh snapshots (all vessels).
 *   track    — replay of ONE vessel's path (see VoyageTrackController).
 *   vessel   — the standalone bar for the empty-map case.
 *
 * There is no `refresh` or `last-updated` here, and since D9 no such controls
 * exist at all. The sensor row's refresh button states the cost of a pull,
 * which a bare icon on the map cannot, and the panel's ON MAP bar carries the
 * stamp, the vessel count and the capture age in words. Copies here meant the
 * map restated three things the sidebar says better, six inches away, while
 * covering the chart to do it — so the descriptors sat registered and unmounted
 * for three phases before being deleted.
 */
const VOYAGERADAR_CONFIG: SensorToolbarConfig = {
  operatorKey: VR_OPERATOR_KEY,
  capabilities: ['pull-refresh', 'clustering', 'track-replay', 'vessel-search', 'priced-actions'],
  live: [
    // FIRST on every bar, ahead of the view controls: it is how you get to a
    // vessel, so it precedes everything that acts on one. This is where the
    // floating search box used to be in reading order too — top-left of the map
    // chrome — so the habit survives the move.
    { id: 'search', order: 5 },
    { id: 'type-filter', order: 10 },
    // Beside the filter, not beside the transport: both decide what the AOI
    // snapshot SHOWS, and it keeps the same button in the same place when the
    // live bar is replaced by the playback or track one.
    { id: 'labels', order: 15 },
    { id: 'clusters', order: 17 },
    // A standing button like the two below it: its drawer says "select a
    // vessel" and carries the price, so tracing is legible as something this
    // sensor can do before a hull is picked rather than after.
    { id: 'track-entry', order: 20 },
    // Immediately after the track entry, and unlike it ALWAYS VISIBLE: the
    // drawer carries the "select a vessel" instruction and the price, so the
    // cost of the cheapest paid action is legible before a hull is picked
    // rather than after. Same order on all three slots below.
    { id: 'port-call', order: 22 },
    // The other half of the same question — port calls is where this hull has
    // been, nearby is who is around it right now — and a standing button on the
    // same terms: its drawer states the price with nothing selected, so the two
    // paid per-vessel actions are legible as a pair rather than one of them
    // being discoverable only after a selection. Kept adjacent on all three
    // slots for the same reason.
    { id: 'nearby', order: 23 },
    // No separator here. There was one at order 25, and it was the only one in
    // the app dividing controls FROM controls — everywhere else the rule fences
    // off something that is not a control (AISstream's live badge, the track
    // slot's identity chip) or the exit (`close`). Its two groups could not be
    // stated in a sentence: `type-filter` on the left and `vessel-history` on
    // the right both just open drawers. Five controls do not need grouping, and
    // an 11px gap that cannot be explained is worse than no gap.
    { id: 'playback-history', order: 30 },
    // Search history (worldwide search): one button, one drawer listing every
    // located vessel with center/remove per row. Renders nothing until a
    // search has located something, so the resting bar is unchanged. This
    // replaced a seven-control per-vessel section — everything else a located
    // vessel needs is the map's normal grammar (marker click → popup +
    // selection → the ordinary Trace button).
    { id: 'vessel-history', order: 35 },
  ],
  playback: [
    { id: 'skip-start', order: 10 },
    { id: 'step-back', order: 20 },
    { id: 'play-pause', order: 30 },
    { id: 'step-forward', order: 40 },
    { id: 'skip-end', order: 50 },
    { id: 'timeline', order: 60 },
    // The same multipliers as the track slot below, and for the same reason: the
    // flip-book now holds each snapshot for a share of a fixed budget rather than
    // running a simulated clock, so speed here means what it means there. The
    // sim-seconds-per-second scale this replaced (1000x … 1x) could not play a
    // week of history in under ten minutes at any setting it offered.
    { id: 'speed', order: 70, options: { speeds: [8, 4, 2, 1, 0.5] } },
    { id: 'labels', order: 80 },
    { id: 'clusters', order: 82 },
    { id: 'type-filter', order: 85 },
    // NO LOOKUP CONTROLS on this bar — not search, not port calls, not nearby.
    // See the note on the track slot below: while a replay owns the map this bar
    // is a transport, and the three questions those controls answer are all
    // about a still map you are reading rather than one that is moving.
    //
    // Port calls and nearby also have a second reason of their own, which is why
    // they were never here: snapshot playback has no selected vessel, so there
    // was never an MMSI for them to ask about. Port calls sat on this bar for a
    // release doing exactly nothing on that account.
    { id: 'sep', order: 95 },
    { id: 'close', order: 100 },
  ],
  // Same geometry as the playback slot on purpose — a track and a snapshot
  // replay swap in the same place, and a transport whose buttons move between
  // the two would be unusable. What differs is only the ends: the identity chip
  // that says WHICH hull, and a way to load a different path for it.
  //
  // Speeds here are plain multipliers (1x = the whole track in ~24s), not the
  // sim-seconds-per-second used by the snapshot flip-book — a track may span an
  // hour or 90 days.
  track: [
    { id: 'track-title', order: 0 },
    { id: 'sep', order: 5 },
    { id: 'skip-start', order: 10 },
    { id: 'step-back', order: 20 },
    { id: 'play-pause', order: 30 },
    { id: 'step-forward', order: 40 },
    { id: 'skip-end', order: 50 },
    { id: 'timeline', order: 60 },
    { id: 'speed', order: 70, options: { speeds: [8, 4, 2, 1, 0.5] } },
    { id: 'labels', order: 80 },
    // NO LOOKUP CONTROLS here either — search, port calls and nearby all came
    // off this bar together.
    //
    // A REPLAY BAR IS A TRANSPORT. Everything left on it moves the clock or
    // changes how the moving thing is drawn; the three that went all ask a
    // question about a still map. Searching for another hull, reading where
    // this one has berthed and asking who is around it are things you do while
    // looking at a scene, not while scrubbing one — and this bar is narrow
    // enough that the transport was competing with them for the same row.
    //
    // The vessel popup does NOT quietly cover for this. Its port-call and
    // nearby buttons only open these same drawers, so with the controls off
    // this bar they would swallow the click — `popupOptsFor` therefore hides
    // both while the drawers are unmounted rather than leaving two dead
    // buttons on the card. Closing the track (the X at the end of this bar)
    // restores the live bar, all three controls, and both popup actions.
    //
    // Where `clusters` sits on the live and playback bars, so the grouping
    // button does not move when a track replaces them. It renders nothing while
    // the dim beside it is on — the two are alternative answers to the same
    // clutter and cannot both apply.
    { id: 'track-clusters', order: 82 },
    { id: 'track-dim', order: 85 },
    { id: 'track-source', order: 90 },
    { id: 'sep', order: 95 },
    { id: 'track-close', order: 100 },
  ],
  // The standalone bar for the EMPTY-MAP case. With a snapshot drawn the same
  // controls ride the live slot above instead (one bar, not two), so this slot
  // mounts only when there is no live bar to join.
  //
  // "Empty map" is NOT only the moment before an AOI's first refresh. A POI task
  // never draws a live layer at any point in its life — its refresh returns the
  // positionless nearby list that hangs off the reticle — so `dataActive` is
  // false throughout and THIS is the only bar a POI task ever shows. Anything a
  // POI user can do from the map, they do from here.
  vessel: [
    // FIRST here of all four slots, and the one that justifies this slot
    // existing at all now: with nothing drawn, searching is the only thing a
    // user can usefully do, and this bar is the only chrome on the map.
    { id: 'search', order: 0 },
    { id: 'vessel-history', order: 5 },
    // The three per-vessel actions, in the live slot's order and adjacency so
    // the group does not rearrange when a snapshot swaps this bar for that one.
    //
    // `track-entry` was ABSENT here while its two siblings were present, which
    // left the empty-map bar offering two thirds of the paid per-vessel set. It
    // is a standing control exactly as they are — its drawer carries the "select
    // a vessel" line and the price — so it has the same claim to this bar, and on
    // a POI task it is the only way to reach a trace from the map at all.
    { id: 'track-entry', order: 20 },
    { id: 'port-call', order: 22 },
    { id: 'nearby', order: 23 },
  ],
};

/**
 * Contributed into the same `SENSOR_TOOLBAR_CONFIGS` multi-token AISstream's
 * config uses, so the host resolves one flat list and cannot tell which file a
 * config came from. Registered alongside `VR_TOOLBAR_CONTROL_PROVIDERS` in
 * `app.module.ts` — a provider now contributes exactly two lines there, its
 * controls and its bars.
 */
export const VR_TOOLBAR_CONFIG_PROVIDERS: Provider[] = [
  { provide: SENSOR_TOOLBAR_CONFIGS, useValue: VOYAGERADAR_CONFIG, multi: true },
];
